const AUTO_MIN = 1800;
const AUTO_MAX = 9000;
const AUTO_BASE = 900;
const AUTO_PER_CHARACTER = 45;

export function responseDuration(text, duration = "auto") {
  if (Number.isFinite(Number(duration)) && duration !== "auto") return Math.max(250,Math.min(60000,Number(duration)));
  const characters=String(text||"").replace(/\s+/g," ").trim().length;
  return Math.max(AUTO_MIN, Math.min(AUTO_MAX, AUTO_BASE + characters * AUTO_PER_CHARACTER));
}
export const RESPONSE_TIMING=Object.freeze({baseMs:AUTO_BASE,msPerCharacter:AUTO_PER_CHARACTER,minMs:AUTO_MIN,maxMs:AUTO_MAX});

export class GameState {
  constructor(initial = {}) {
    this.values = { ...initial };
    this.seenResponses = new Set(initial.seenResponses || []);
  }
  get(key) { return this.values[key]; }
  set(key, value) { this.values[key] = value; return value; }
  hasSeen(id) { return this.seenResponses.has(id); }
  markSeen(id) { this.seenResponses.add(id); }
  snapshot() { return { ...this.values, seenResponses: [...this.seenResponses] }; }
}

export function conditionMatches(condition, state) {
  if (!condition?.key) return true;
  const actual = state.get(condition.key), expected = condition.value;
  switch (condition.operator || "equals") {
    case "not-equals": return actual !== expected;
    case "truthy": return !!actual;
    case "falsy": return !actual;
    case "greater-than": return Number(actual) > Number(expected);
    case "less-than": return Number(actual) < Number(expected);
    default: return actual === expected;
  }
}

export class ActionRunner {
  constructor({ responses, state = new GameState(), responseManager, dispatch = () => {}, transition = dispatch, debug = false, error = console.error }) {
    this.responses = responses || {};
    this.state = state;
    this.responseManager = responseManager;
    this.dispatch = dispatch;
    this.transition = transition;
    this.debug = debug;
    this.error = error;
  }
  isInterruptible(action){if(action?.interruptible===false)return false;if(action?.type==="response")return this.responses[action.responseId]?.interruptible!==false;return true}
  async run(action, context = {}) {
    const operation=context.operation;if(operation?.signal.aborted)return{aborted:true};
    if (!action || action.type === "none" || action.type === "walk-only") return;
    if(action.interruptible===false&&operation)operation.interruptible=false;
    switch (action.type) {
      case "response": {
        const response = this.responses[action.responseId];
        if (!response) {
          this.error(`Missing responseId: ${action.responseId || "(empty)"}`);
          if (this.debug) await this.responseManager?.show(action.responseId || "missing-response", { speaker: "debug", text: `[Missing response: ${action.responseId || "empty id"}]`, blocking: false, duration: 2200 }, this.state,{operation});
          return;
        }
        if (response.condition && !conditionMatches(response.condition, this.state)) return;
        if(response.interruptible===false&&operation)operation.interruptible=false;
        operation?.setStep(`response:${action.responseId}`);
        await this.responseManager?.show(action.responseId, response, this.state,{operation});
        if (operation?.signal.aborted)return{aborted:true};
        if (response.followUpAction) await this.run(response.followUpAction, context);
        return;
      }
      case "set-state":
        operation?.setStep(`set-state:${action.key}`);
        this.state.set(action.key, action.value);
        return;
      case "sequence":
        for (let index=0;index<(action.steps||[]).length;index++){if(operation?.signal.aborted)return{aborted:true};operation?.setStep(`sequence:${index}`);await this.run(action.steps[index], context)}
        return;
      case "conditional":
        operation?.setStep("conditional");
        return this.run(conditionMatches({ key: action.key, operator: action.operator, value: action.value }, this.state) ? action.then : action.else, context);
      case "scene-transition":
        if(operation?.signal.aborted)return{aborted:true};operation?.commit("scene-transition-started");
        return this.transition(action, context);
      case "custom-event":
        if(operation?.signal.aborted)return{aborted:true};operation?.setStep("custom-event");
        return this.dispatch(action, context);
      case "lift-sequence":
        if(operation?.signal.aborted)return{aborted:true};operation?.setStep(`lift-sequence:${action.propId||"missing"}`);
        return this.dispatch(action, context);
      default:
        this.error(`Unsupported action type: ${action.type}`);
        if (this.debug) await this.responseManager?.show(`unsupported-${action.type}`, { speaker: "debug", text: `[Unsupported action: ${action.type}]`, blocking: false }, this.state,{operation});
    }
  }
}

export class ResponseManager {
  constructor({ root = document.body } = {}) {
    this.queue = [];
    this.current = null;
    this.blocking = false;this.generation=0;this.lastDismissReason=null;
    this.element = document.createElement("section");
    this.element.className = "protagonist-response";
    this.element.hidden = true;
    this.element.setAttribute("role", "status");
    this.element.setAttribute("aria-live", "polite");
    this.element.setAttribute("aria-atomic", "true");
    this.element.innerHTML = `<strong class="protagonist-response__speaker"></strong><div class="protagonist-response__text"></div>`;
    root.append(this.element);
    this.onKey = event => {
      if (!this.current || !["Enter", " ", "Escape"].includes(event.key)) return;
      if (["Enter", " "].includes(event.key)) event.preventDefault();
      const menu=document.querySelector(".interaction-menu");if(event.key==="Escape"&&menu&&!menu.hidden)return;
      this.dismiss({reason:`keyboard-${event.key}`});
    };
    this.element.addEventListener("pointerdown", event => { event.stopPropagation(); this.dismiss({reason:"response-click"}); });
    document.addEventListener("keydown", this.onKey);
  }
  show(id, response, state,{operation=null}={}) {
    return new Promise(resolve => {
      const seen = state.hasSeen(id);
      if (seen && response.once && !response.repeatText) return resolve({ skipped: true });
      if(operation?.signal.aborted)return resolve({aborted:true});
      const item={ id, response:{interruptible:true,...response}, state, resolve, text: seen && response.repeatText ? response.repeatText : response.text,operationId:operation?.id||null,signal:operation?.signal||null };
      if(item.signal)item.signal.addEventListener("abort",()=>this.cancelOperation(item.operationId,item.signal.reason||"operation-aborted"),{once:true});
      this.queue.push(item);
      this.next();
    });
  }
  next() {
    if (this.current || !this.queue.length) return;
    this.current = this.queue.shift();
    const { id, response, state, text, resolve } = this.current;
    state.markSeen(id);
    this.blocking = response.blocking !== false;
    const speaker = this.element.querySelector(".protagonist-response__speaker");
    speaker.textContent = response.speaker || "";
    speaker.hidden = !response.speaker;
    this.element.querySelector(".protagonist-response__text").textContent = text || "";
    this.element.hidden = false;
    const generation=++this.generation;
    this.timer = setTimeout(() => {if(this.current&&this.generation===generation)this.dismiss({reason:"auto-duration"})}, responseDuration(text, response.duration));
    this.current.resolve = resolve;
  }
  dismiss({reason="manual",advance=true}={}) {
    if (!this.current) return;
    clearTimeout(this.timer);this.timer=null;++this.generation;
    const current = this.current;
    this.current = null;
    this.blocking = false;
    this.element.hidden = true;
    this.lastDismissReason=reason;current.resolve({ skipped: false,reason });
    if(advance)this.next();
  }
  cancelOperation(operationId,reason="operation-aborted"){if(operationId==null)return;const kept=[];for(const item of this.queue){if(item.operationId===operationId)item.resolve({aborted:true,reason});else kept.push(item)}this.queue=kept;if(this.current?.operationId===operationId)this.dismiss({reason,advance:false});this.next()}
  interruptCurrent(reason="player-input"){if(!this.current)return true;if(this.current.response.interruptible===false)return false;this.cancelOperation(this.current.operationId,reason);return true}
  isBlocking() { return this.blocking; }
  isInterruptible(){return this.current?.response.interruptible!==false}
  debugState(){return{currentResponseId:this.current?.id||null,currentOperationId:this.current?.operationId||null,blocking:this.blocking,interruptible:this.isInterruptible(),queued:this.queue.length,generation:this.generation,lastDismissReason:this.lastDismissReason}}
  dispose() {
    clearTimeout(this.timer);this.timer=null;++this.generation;
    document.removeEventListener("keydown", this.onKey);
    this.current?.resolve({ disposed: true });this.current=null;
    this.queue.forEach(item => item.resolve({ disposed: true }));this.queue=[];this.blocking=false;
    this.element.remove();
  }
}

export function ensureResponseStyles() {
  if (document.querySelector("#protagonist-response-styles")) return;
  const style = document.createElement("style");
  style.id = "protagonist-response-styles";
  style.textContent = `.protagonist-response{position:fixed;z-index:1900;left:50%;bottom:max(22px,env(safe-area-inset-bottom));transform:translateX(-50%);width:min(760px,calc(100vw - 32px));padding:12px 18px;border-top:1px solid #78909c66;background:#050a0ee8;box-shadow:0 8px 30px #000b;color:#eef7f8;text-align:center;line-height:1.5;white-space:pre-wrap;pointer-events:auto}.protagonist-response[hidden]{display:none}.protagonist-response__speaker{display:block;margin-bottom:3px;color:#e8a66e;font-size:.78em;letter-spacing:.12em;text-transform:uppercase}`;
  document.head.append(style);
}
